import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ThreeColumnPage } from "../../Components/ThreeColumnPage";
import { SubTab } from "../../Components/ThreeColumnPage";
import { NavigationNode } from "@aidkitorg/types/lib/translation/permissions";
import { useAsyncEffect } from "../../Hooks/AsyncEffect";
import { usePost } from "../../API";

type RightPaneElement = {
  key: string;
  name: string;
  children: JSX.Element;
  show?: boolean;
  order?: number;
};

type AIPageContextValue = {
  registerRightPaneElement: (element: RightPaneElement) => void;
  unregisterRightPaneElement: (key: string) => void;
};

const AIPageContext = createContext<AIPageContextValue | null>(null);

export function useAIPage() {
  return useContext(AIPageContext);
}

/**
  * Use this like a useEffect() hook:
  *
  * useAIPageRightPaneElement(() => <>
  *   <span>Last Message: {lastChatMessage}</span>
  * </>, [lastChatMessage])
  *
  */
export function useAIPageRightPaneElement(element: () => RightPaneElement | null, dependencies: any[]) {
  const context = useAIPage();
  // wrap in memo to account for non-memoized elements.
  // just preventing a footgun!
  const elementRef = useMemo(element, [...dependencies]);

  // Register/unregister lifecycle — only re-runs when the key changes,
  // preventing the unregister→register cycle that previously fired on
  // every render due to the full element object being a dependency.
  useEffect(() => {
    if (!context || !elementRef) return;
    const currentKey = elementRef.key;
    context.registerRightPaneElement(elementRef);
    return () => context.unregisterRightPaneElement(currentKey);
  }, [context, elementRef]);

  return elementRef;
}

export function AIPage(props: React.PropsWithChildren<{}>) {
  const [nav, setNav] = useState<NavigationNode[]>([]);
  const [rightPaneElements, setRightPaneElements] = useState<Record<string, RightPaneElement>>({});

  const getNav = usePost('/program/admin/ai/navigation');

  useAsyncEffect(async () => {
    const result = await getNav({});
    if (result && Array.isArray(result.nav)) {
      setNav(result.nav);
    }
  }, [])

  const registerRightPaneElement = useCallback((element: RightPaneElement) => {
    setRightPaneElements((prev) => {
      const existing = prev[element.key];
      if (
        existing &&
        existing.name === element.name &&
        existing.show === element.show &&
        existing.order === element.order &&
        existing.children === element.children
      ) {
        return prev;
      }
      return { ...prev, [element.key]: element };
    });
  }, []);

  const unregisterRightPaneElement = useCallback((key: string) => {
    setRightPaneElements((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const subtabs = useMemo<SubTab[]>(() => {
    return Object.values(rightPaneElements)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((element) => ({
        key: element.key,
        name: element.name,
        children: element.children,
        show: element.show ?? true,
      }));
  }, [rightPaneElements]);

  const contextValue = useMemo<AIPageContextValue>(() => ({
    registerRightPaneElement,
    unregisterRightPaneElement,
  }), [registerRightPaneElement, unregisterRightPaneElement]);

  return <AIPageContext.Provider value={contextValue}>
    <ThreeColumnPage
      title="AI Admin"
      nav={nav}
      subtabs={subtabs}
      rightPanelControlled={false}
      main={
        <div>
          {props.children}
        </div>
      }
    />
  </AIPageContext.Provider>
}
